import { useState, useCallback, useRef } from 'react';
import { useLatest } from './use-latest';
import type { Message, ApiMessage, ContentPart, ChatSettings, ToolCallWithResult, DataSource } from '../types';
import { streamV1Chat, archiveExchange, archiveToMemory, maybeCaptureRemembered, hashTurn, type GenerationSettings, type StreamV1ChatResult } from '../api';
import { estimateConversationTokens } from '../utils';
import { llmApiClient } from '../api/network';

/** Threshold for tool call result size (bytes) that triggers chain-breaking */
const CHAIN_BREAK_MIN_BYTES = 4096;
/** Context usage ratio that triggers automatic compression */
const AUTO_COMPRESS_THRESHOLD = 0.85;

interface UseChatOptions {
  apiUrl: string;
  apiKey?: string;
  model: string;
  modelCapabilities?: string[];
  /** MCP server IDs from mcp.json to enable (e.g. ["mcp/mc-server-hub"]) */
  mcpServers?: string[];
  /** Signpost the owner-only `knowledge` server; see `StreamV1ChatOptions`. */
  includeKnowledgeSummary?: boolean;
  /** Signpost the owner-only `apple-bridge` server; see `StreamV1ChatOptions`. */
  includeAppleSummary?: boolean;
  /** External data sources available via the data_lookup MCP tool */
  dataSources?: DataSource[];
  /**
   * Session system prompt (tdd-0044), seeded by the active persona. Injected as
   * the app's first `role:'system'` message — ahead of the data-sources prompt,
   * and ahead of the LB's Brain-recall block (the LB prepends recall server-side).
   */
  systemPrompt?: string;
  /** Recall long-term memory (Brain) and inject relevant facts into the system prompt each turn */
  enableMemory?: boolean;
  /** Brain workspace shard to read from when enableMemory is set (default "personal") */
  brainWorkspace?: string;
  /** Conversation this turn belongs to, for undelivered-turn write-back (tdd-0090). */
  sessionId?: string;
  /** Whether that conversation may be persisted server-side at all (tdd-0090). */
  persistable?: boolean;
  /** Model's loaded context length (for chain-breaking and auto-compress thresholds) */
  maxContextTokens?: number;
  settings?: ChatSettings;
  onError?: (error: Error) => void;
  onToolCall?: (toolCall: ToolCallWithResult) => void;
}

/**
 * Build a system prompt addendum describing available data sources.
 */
function buildDataSourcesPrompt(dataSources: DataSource[]): string {
  const sourceList = dataSources
    .map(ds => `  - ${ds.name}: ${ds.description} (url: ${ds.url})`)
    .join('\n');

  return [
    '',
    '## Available Data Sources',
    'You have access to external data sources via the `data_lookup` tool. Call it with the URL of a data source to retrieve information.',
    'Available sources:',
    sourceList,
    '',
    'Guidelines:',
    '- Use `data_lookup` to fetch data rather than guessing or making up information.',
    '- Use the `query` parameter to filter results when possible.',
    '- Use the `fields` parameter to request only the fields you need.',
    '- Use the `limit` parameter to cap result count (default 25).',
    '- After receiving data, extract relevant information into your response immediately.',
    '- Do not refer back to raw data payloads in subsequent turns — work from your own summary.',
  ].join('\n');
}

/**
 * Estimate the byte size of tool call results in a completed response.
 */
function estimateToolResultBytes(toolCalls: ToolCallWithResult[]): number {
  let bytes = 0;
  for (const tc of toolCalls) {
    if (tc.result) {
      for (const content of tc.result.content) {
        if (content.text) {
          bytes += content.text.length;
        }
      }
    }
  }
  return bytes;
}

/**
 * Determine if this turn's tool results are large enough to warrant breaking
 * the response chain on the next turn.
 */
function shouldBreakChain(toolCalls: ToolCallWithResult[], maxContextTokens: number): boolean {
  const resultBytes = estimateToolResultBytes(toolCalls);
  // Break if result exceeds 4KB or 50% of model context (in estimated chars), whichever is larger
  const contextCharsThreshold = maxContextTokens > 0 ? (maxContextTokens * 4) * 0.5 : Infinity;
  const threshold = Math.max(CHAIN_BREAK_MIN_BYTES, contextCharsThreshold);
  return resultBytes >= threshold;
}

/**
 * Unique message id.
 *
 * `Date.now()` alone is not enough: two messages created in the same
 * millisecond collide, which React reports as "two children with the same key"
 * and resolves by duplicating or dropping one. It happens routinely — a user
 * turn and its assistant placeholder are created back to back, and programmatic
 * turns (an elicited form submitting, an auto-summary) can fire faster still.
 *
 * The counter guarantees uniqueness within a session; the timestamp keeps ids
 * sortable and readable, which the persisted-session format relies on.
 */
let messageSeq = 0;
function nextMessageId(prefix: string): string {
  return `${prefix}-${Date.now()}-${++messageSeq}`;
}

export function useChat({ apiUrl, apiKey, model, modelCapabilities, mcpServers, includeKnowledgeSummary, includeAppleSummary, dataSources, systemPrompt, enableMemory, brainWorkspace, sessionId, persistable, maxContextTokens, settings, onError, onToolCall }: UseChatOptions) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const currentAssistantIdRef = useRef<string | null>(null);
  /** The user message whose attachments this turn's LB report applies to. */
  const currentUserMessageIdRef = useRef<string | null>(null);
  // Keep a ref to current messages for synchronous access
  const messagesRef = useRef<Message[]>([]);
  // Track the latest response_id for multi-turn v1 API continuity
  const previousResponseIdRef = useRef<string | undefined>(undefined);
  // Flag: should the next turn break the response chain?
  const shouldBreakChainRef = useRef(false);
  // Guard against concurrent auto-compress
  const isAutoCompressingRef = useRef(false);
  /**
   * The API content each user turn was sent with, keyed by message id.
   *
   * `Message.content` holds display text; attachments ride in the multi-part
   * API content. Kept here so a retry reproduces the original request exactly.
   *
   * Only the entries needed for a retry are held: it is cleared alongside the
   * messages themselves when the conversation is replaced.
   */
  const apiContentByMessageIdRef = useRef<Map<string, string | ContentPart[]>>(new Map());
  // Props the streaming callbacks read LATE — a response outlives the render
  // that started it, so a plain closure would see stale values. `useLatest`
  // mirrors them without writing a ref during render; see its own doc comment.
  const settingsRef = useLatest(settings);
  const modelCapabilitiesRef = useLatest(modelCapabilities);
  const mcpServersRef = useLatest(mcpServers);
  const includeKnowledgeSummaryRef = useLatest(includeKnowledgeSummary);
  const includeAppleSummaryRef = useLatest(includeAppleSummary);
  const dataSourcesRef = useLatest(dataSources);
  const systemPromptRef = useLatest(systemPrompt);
  const enableMemoryRef = useLatest(enableMemory);
  // Read through refs like every other per-turn option here: the send
  // closure is built once, and a session switch mid-stream must not
  // retarget the in-flight turn's write-back at the new conversation.
  const sessionIdRef = useLatest(sessionId);
  const persistableRef = useLatest(persistable);
  const brainWorkspaceRef = useLatest(brainWorkspace);
  const maxContextTokensRef = useLatest(maxContextTokens ?? 0);
  // Callbacks, kept out of the useCallback dep arrays for the same reason.
  const onErrorRef = useLatest(onError);
  const onToolCallRef = useLatest(onToolCall);

  // Convert ChatSettings to GenerationSettings for API.
  // When no task profile is active (custom/null), return undefined so LM Studio
  // uses its own per-model defaults rather than our hardcoded values.
  const getGenerationSettings = useCallback((): GenerationSettings | undefined => {
    const s = settingsRef.current;
    if (!s || s.taskProfileKey === null) return undefined;
    return {
      temperature: s.temperature,
      maxTokens: s.maxTokens,
      topP: s.topP,
      topK: s.topK,
      minP: s.minP,
      repeatPenalty: s.repeatPenalty,
      presencePenalty: s.presencePenalty,
      frequencyPenalty: s.frequencyPenalty,
      repetitionPenalty: s.repetitionPenalty,
      seed: s.seed,
      stop: s.stop,
    };
    // The ref OBJECT is stable for the hook's lifetime — only `.current`
    // changes — so listing it satisfies exhaustive-deps without ever
    // invalidating this callback. Same for every other `useLatest` ref below.
  }, [settingsRef]);

  // Helper to update both state and ref
  const updateMessages = useCallback((updater: (prev: Message[]) => Message[]) => {
    setMessages((prev) => {
      const next = updater(prev);
      messagesRef.current = next;
      return next;
    });
  }, []);

  /**
   * Auto-compress conversation when context usage >= 85%.
   * Runs in the background after a response completes.
   */
  const maybeAutoCompress = useCallback(async () => {
    const ctx = maxContextTokensRef.current;
    if (ctx <= 0) return; // No context limit known
    if (isAutoCompressingRef.current) return;

    const currentMessages = messagesRef.current;
    const currentTokens = estimateConversationTokens(currentMessages);
    const usage = currentTokens / ctx;

    if (usage < AUTO_COMPRESS_THRESHOLD) return;

    isAutoCompressingRef.current = true;

    try {
      // Build conversation text for summarization
      const conversationText = currentMessages
        .filter(m => m.role === 'user' || m.role === 'assistant')
        .map(m => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content}`)
        .join('\n\n');

      if (!conversationText.trim()) return;

      const systemPrompt = `You are a conversation summarizer. Create a concise but detailed summary of this conversation that preserves:
- Key decisions and conclusions
- Technical details, code snippets, and solutions
- Important context and constraints
- Current task state

Format as a system message for continuing the conversation. Be thorough but concise — aim for roughly 20% of the original length.`;

      interface CompletionResponse {
        choices?: Array<{ message?: { content?: string } }>;
      }

      const { data } = await llmApiClient.post<CompletionResponse>(
        `${apiUrl}/v1/chat/completions`,
        {
          model,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `Summarize this conversation:\n\n${conversationText}` },
          ],
          temperature: 0.3,
        },
        {
          timeout: 120_000,
          headers: {
            ...(apiKey && { Authorization: `Bearer ${apiKey}` }),
          },
        }
      );

      const summary = data.choices?.[0]?.message?.content;
      if (!summary) return;

      // Keep the last 2 messages (most recent exchange) for continuity,
      // replace everything before with the summary
      const keepCount = Math.min(2, currentMessages.length);
      const keptMessages = currentMessages.slice(-keepCount);

      const summaryMessage: Message = {
        id: nextMessageId('auto-summary'),
        role: 'system',
        content: `[Auto-compressed conversation summary]\n\n${summary}`,
        createdAt: Date.now(),
      };

      const newMessages = [summaryMessage, ...keptMessages];

      // Break the response chain since we've rewritten history
      previousResponseIdRef.current = undefined;
      messagesRef.current = newMessages;
      setMessages(newMessages);
    } catch (error) {
      console.warn('[auto-compress] Failed to compress conversation:', error);
    } finally {
      isAutoCompressingRef.current = false;
    }
  }, [apiUrl, apiKey, model, maxContextTokensRef]);

  // Core streaming function — used by append and reload
  const streamResponse = useCallback(
    async (apiMessages: ApiMessage[], assistantId: string) => {
      let assistantContent = '';
      const toolCallsForMessage: ToolCallWithResult[] = [];

      // Latest user message — the query for memory recall and the key for the
      // post-completion capture. Computed once; flattens multi-part content to text.
      const memoryQuery = (() => {
        const lastUser = [...apiMessages].reverse().find(m => m.role === 'user');
        if (typeof lastUser?.content === 'string') return lastUser.content;
        if (Array.isArray(lastUser?.content)) {
          return lastUser.content.map(p => (p.type === 'text' ? p.text : '')).join(' ');
        }
        return '';
      })();

      // If chain-breaking was flagged from the previous turn, clear the response ID
      // so LM Studio starts a fresh context (purging large tool results from server-side history)
      if (shouldBreakChainRef.current) {
        previousResponseIdRef.current = undefined;
        shouldBreakChainRef.current = false;
      }

      // Inject data sources system prompt if configured and not already present
      const ds = dataSourcesRef.current;
      if (ds && ds.length > 0) {
        const hasDataSourcePrompt = apiMessages.some(
          m => m.role === 'system' && typeof m.content === 'string' && m.content.includes('## Available Data Sources')
        );
        if (!hasDataSourcePrompt) {
          apiMessages = [
            { role: 'system', content: buildDataSourcesPrompt(ds) },
            ...apiMessages,
          ];
        }
      }

      // Inject the persona/session system prompt (tdd-0044) as the FIRST system
      // message — ahead of data-sources. The LB later prepends its Brain-recall
      // block server-side, yielding model order: [recall][persona][data-sources]
      // [...turns]. Recall is context, persona is instruction; one layer each, no
      // double-inject. Prepend (not append) so it leads; guarded for idempotency
      // across the multi-turn tool loop.
      const sessionPrompt = systemPromptRef.current?.trim();
      if (sessionPrompt) {
        const alreadyPresent = apiMessages.some(
          m => m.role === 'system' && m.content === sessionPrompt,
        );
        if (!alreadyPresent) {
          apiMessages = [
            { role: 'system', content: sessionPrompt },
            ...apiMessages,
          ];
        }
      }

      // Recall is centralized at the LLM LB (tdd-0042): we send x-brain-recall:on
      // (when memory is enabled) and the LB injects the user's relevant memories
      // server-side, telling us what it recalled via a `memory.recalled` SSE event.
      // These are captured here and attached to the assistant message for the UI.
      let recalledMemories: string[] = [];
      let recalledQuery: string | undefined;

      abortControllerRef.current = new AbortController();

      await streamV1Chat({
        apiUrl,
        apiKey,
        messages: apiMessages,
        model,
        modelCapabilities: modelCapabilitiesRef.current,
        previousResponseId: previousResponseIdRef.current,
        mcpServers: mcpServersRef.current,
        includeKnowledgeSummary: includeKnowledgeSummaryRef.current,
        includeAppleSummary: includeAppleSummaryRef.current,
        settings: getGenerationSettings(),
        reasoningLevel: settingsRef.current?.reasoningLevel,
        signal: abortControllerRef.current.signal,
        enableMemory: enableMemoryRef.current,
        sessionId: sessionIdRef.current,
        persistable: persistableRef.current,
        onRecalledMemories: (facts, query) => {
          recalledMemories = facts;
          recalledQuery = query;
        },
        // The LB reports what it did with each attachment (tdd-0062). This lands
        // on the USER's message, not the assistant's, because that is where the
        // attachment chips live — the user needs to see "indexed" next to the
        // file they attached, not in the reply.
        onAttachmentsProcessed: (outcomes) => {
          updateMessages((prev) =>
            prev.map((m) => {
              if (!m.attachments?.length) return m;
              // Only the message being sent this turn can be affected; earlier
              // messages keep whatever state they were given at the time.
              if (m.id !== currentUserMessageIdRef.current) return m;
              return {
                ...m,
                attachments: m.attachments.map((a) => {
                  const outcome = outcomes.find((o) => o.filename === a.name);
                  return outcome ? { ...a, outcome } : a;
                }),
              };
            })
          );
        },
        onChunk: (content) => {
          assistantContent += content;
          updateMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: assistantContent } : m
            )
          );
        },
        onToolCallStart: (toolName) => {
          // Add a pending tool call to the assistant message for live UI
          const pendingToolCall: ToolCallWithResult = {
            id: nextMessageId('tc-pending'),
            name: toolName,
            arguments: {},
            status: 'running',
          };
          toolCallsForMessage.push(pendingToolCall);
          updateMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, toolCalls: [...toolCallsForMessage] }
                : m
            )
          );
        },
        onToolCallArguments: (toolName, args) => {
          // Fill in the pending card the moment the arguments arrive, instead
          // of leaving it showing `{}` until the call finishes.
          //
          // Matched on the last RUNNING call with this name, not the first: a
          // turn can run the same tool more than once, and the earlier ones
          // have already been reconciled by onToolCallComplete.
          const idx = toolCallsForMessage.findLastIndex(
            (tc) => tc.status === 'running' && tc.name === toolName,
          );
          if (idx < 0) return;

          const existing = toolCallsForMessage[idx];
          if (!existing) return;
          toolCallsForMessage[idx] = { ...existing, arguments: args };
          updateMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, toolCalls: [...toolCallsForMessage] }
                : m
            )
          );
        },
        onToolCallComplete: (toolCall) => {
          // Replace the pending tool call with the completed one
          const pendingIdx = toolCallsForMessage.findIndex(
            tc => tc.status === 'running' && tc.name === toolCall.name
          );
          if (pendingIdx >= 0) {
            toolCallsForMessage[pendingIdx] = toolCall;
          } else {
            toolCallsForMessage.push(toolCall);
          }

          updateMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? { ...m, toolCalls: [...toolCallsForMessage] }
                : m
            )
          );

          onToolCallRef.current?.(toolCall);
        },
        onComplete: (result: StreamV1ChatResult) => {
          // Track response_id for multi-turn continuity
          if (result.responseId) {
            previousResponseIdRef.current = result.responseId;
          }

          // Final update with all tool calls resolved
          if (toolCallsForMessage.length > 0) {
            updateMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, toolCalls: [...toolCallsForMessage] }
                  : m
              )
            );

            // Check if tool results were large enough to break the chain on the next turn
            if (shouldBreakChain(toolCallsForMessage, maxContextTokensRef.current)) {
              shouldBreakChainRef.current = true;
            }
          }

          setIsLoading(false);
          abortControllerRef.current = null;
          currentAssistantIdRef.current = null;

          // Attach recalled memories (+ the query they came from) to the assistant
          // message for the recall bubble and its "edit in Brain" link.
          if (recalledMemories.length > 0) {
            updateMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId
                  ? { ...m, recalledMemories, recalledQuery: recalledQuery ?? memoryQuery }
                  : m
              )
            );
          }

          // Auto-promote the completed exchange to long-term memory (Brain), keyed on
          // content so re-sends/reloads are idempotent. Best-effort, fire-and-forget.
          if (enableMemoryRef.current && memoryQuery && assistantContent.trim()) {
            void archiveExchange(
              memoryQuery,
              assistantContent,
              hashTurn(memoryQuery, assistantContent),
              brainWorkspaceRef.current ?? 'personal',
            );
          }

          // Check if we should auto-compress (fire-and-forget)
          maybeAutoCompress();
        },
        onError: (error) => {
          setIsLoading(false);
          abortControllerRef.current = null;
          currentAssistantIdRef.current = null;
          const userMessageId = currentUserMessageIdRef.current;
          const aborted = error.name === 'AbortError';
          updateMessages((prev) =>
            prev
              // Remove the empty assistant message on error. Anything already
              // streamed is kept — a truncated reply is still worth reading,
              // and deleting it would lose content the user watched arrive.
              .filter((m) => !(m.id === assistantId && !m.content))
              // Mark the user's turn as unanswered so the UI can offer a retry.
              // Not on an abort: the user stopped it themselves, and offering
              // to retry something they just cancelled reads as a failure.
              .map((m) =>
                !aborted && m.id === userMessageId
                  ? { ...m, failed: { message: error.message || 'Something went wrong' } }
                  : m
              )
          );
          if (!aborted) {
            onErrorRef.current?.(error);
          }
        },
      });
    },
    [
      apiUrl,
      apiKey,
      model,
      getGenerationSettings,
      updateMessages,
      maybeAutoCompress,
      // All stable ref objects (see `useLatest`); listed so exhaustive-deps
      // stays meaningful rather than suppressed. None of them invalidate this.
      brainWorkspaceRef,
      dataSourcesRef,
      enableMemoryRef,
      includeAppleSummaryRef,
      includeKnowledgeSummaryRef,
      maxContextTokensRef,
      mcpServersRef,
      modelCapabilitiesRef,
      onErrorRef,
      onToolCallRef,
      settingsRef,
      systemPromptRef,
    ]
  );

  // Append a message with optional multi-part content for the API
  const append = useCallback(
    async (message: Message, apiContent?: string | ContentPart[]) => {
      // If there's an in-progress generation, cancel it and remove empty placeholder
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;

        // Remove empty assistant placeholder if it exists
        const emptyAssistantId = currentAssistantIdRef.current;
        if (emptyAssistantId) {
          updateMessages((prev) => {
            const msg = prev.find((m) => m.id === emptyAssistantId);
            // Only remove if it's empty (no content yet)
            if (msg && !msg.content) {
              return prev.filter((m) => m.id !== emptyAssistantId);
            }
            return prev;
          });
        }
      }

      const assistantId = message.role === 'user' ? nextMessageId('assistant') : null;

      // If the user said "remember …", capture that fact as a semantic memory.
      // Show the "saved" bubble immediately (id resolves async, enabling undo).
      // Fires on the new message only (not on reload). Best-effort.
      const messageId = message.id;
      if (enableMemoryRef.current && message.role === 'user' && typeof message.content === 'string') {
        void maybeCaptureRemembered(message.content, brainWorkspaceRef.current ?? 'personal').then(
          (saved) => {
            if (saved) {
              updateMessages((prev) =>
                prev.map((m) => (m.id === messageId ? { ...m, memorySaved: saved } : m)),
              );
            }
          },
        );
      }

      // Build messages synchronously from the ref, then update state
      let currentMessages = messagesRef.current;

      // Add the new message
      currentMessages = [...currentMessages, message];

      // Build API messages (excluding any assistant placeholder we're about to add)
      const apiMessages: ApiMessage[] = currentMessages.map((m) => {
        if (m.id === message.id && apiContent) {
          return { role: m.role, content: apiContent };
        }
        return { role: m.role, content: m.content };
      });

      // If this is a user message, also add the assistant placeholder
      if (assistantId) {
        currentMessages = [
          ...currentMessages,
          { id: assistantId, role: 'assistant' as const, content: '', createdAt: Date.now(), modelId: model },
        ];
      }

      // Update ref immediately for synchronous access
      messagesRef.current = currentMessages;
      // Update state for React rendering
      setMessages(currentMessages);

      // Remember the multi-part content this turn is sent with, so a retry can
      // reproduce the request exactly. `Message.content` is the display text
      // only — attachments live in the API content — so retrying from it would
      // silently drop them and ask the model about a file it never received.
      if (apiContent) {
        apiContentByMessageIdRef.current.set(message.id, apiContent);
      }

      if (message.role === 'user' && assistantId) {
        setIsLoading(true);
        currentAssistantIdRef.current = assistantId;
        // Remembered so the LB's attachment report can be applied to the right
        // user message — the outcomes arrive mid-stream, after this returns.
        currentUserMessageIdRef.current = message.id;
        await streamResponse(apiMessages, assistantId);
      }

      return message.id;
    },
    // The two refs are stable objects (see `useLatest`) and never invalidate.
    [model, updateMessages, streamResponse, brainWorkspaceRef, enableMemoryRef]
  );

  // Convenience method for appending with multi-part content (files)
  const appendWithFiles = useCallback(
    (message: Message, apiContent: ContentPart[]) => {
      return append(message, apiContent);
    },
    [append]
  );

  const handleSubmit = useCallback(
    (e?: { preventDefault?: () => void }) => {
      e?.preventDefault?.();

      if (!input.trim()) return;

      const userMessage: Message = {
        id: nextMessageId('user'),
        role: 'user',
        content: input.trim(),
        createdAt: Date.now(),
      };

      setInput('');
      append(userMessage);
    },
    [input, append]
  );

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  }, []);

  const reload = useCallback(async () => {
    const currentMessages = messagesRef.current;
    if (currentMessages.length === 0) return;

    // Find the last user message
    const lastUserIndex = currentMessages.findLastIndex((m) => m.role === 'user');
    if (lastUserIndex === -1) return;

    // Remove messages after the last user message
    const messagesUpToLastUser = currentMessages.slice(0, lastUserIndex + 1);

    // Clear response_id — we're regenerating so server-side history won't match
    previousResponseIdRef.current = undefined;

    // Regenerate response
    setIsLoading(true);
    const assistantId = nextMessageId('assistant');

    // Update with messages up to last user + empty assistant placeholder
    const newMessages = [
      ...messagesUpToLastUser,
      { id: assistantId, role: 'assistant' as const, content: '', createdAt: Date.now(), modelId: model },
    ];
    messagesRef.current = newMessages;
    setMessages(newMessages);

    currentAssistantIdRef.current = assistantId;

    // Convert to API messages (exclude assistant placeholder)
    const apiMessages: ApiMessage[] = messagesUpToLastUser.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    await streamResponse(apiMessages, assistantId);
  }, [model, streamResponse]);

  /**
   * Re-send a user turn that never got a reply.
   *
   * Rewinds to just after that message and streams again, so the model sees the
   * same conversation it saw the first time — retrying from a later point would
   * silently include turns the failed request never contained.
   *
   * `previous_response_id` is cleared for the same reason `reload` clears it:
   * the server's copy of this conversation no longer matches what we are about
   * to send, and continuing from a stale chain reintroduces the turn twice.
   */
  const retry = useCallback(
    async (messageId: string) => {
      const currentMessages = messagesRef.current;
      const index = currentMessages.findIndex((m) => m.id === messageId);
      if (index === -1) return;

      const target = currentMessages[index];
      if (!target || target.role !== 'user') return;

      // Clear the failure BEFORE re-sending: leaving it set would show the
      // error and a retry button next to a turn that is visibly in flight.
      const upTo = currentMessages.slice(0, index + 1).map((m) =>
        m.id === messageId ? { ...m, failed: undefined } : m
      );

      previousResponseIdRef.current = undefined;

      const assistantId = nextMessageId('assistant');
      const newMessages: Message[] = [
        ...upTo,
        { id: assistantId, role: 'assistant' as const, content: '', createdAt: Date.now(), modelId: model },
      ];
      messagesRef.current = newMessages;
      setMessages(newMessages);

      setIsLoading(true);
      currentAssistantIdRef.current = assistantId;
      // The LB's attachment report belongs to this turn's user message, same as
      // on the original send.
      currentUserMessageIdRef.current = messageId;

      const apiMessages: ApiMessage[] = upTo.map((m) => ({
        role: m.role,
        // The content this turn was originally sent with, attachments included.
        content: apiContentByMessageIdRef.current.get(m.id) ?? m.content,
      }));

      await streamResponse(apiMessages, assistantId);
    },
    [model, streamResponse]
  );

  // Wrap setMessages to keep ref in sync
  const setMessagesWithRef = useCallback((messagesOrUpdater: Message[] | ((prev: Message[]) => Message[])) => {
    if (typeof messagesOrUpdater === 'function') {
      updateMessages(messagesOrUpdater);
    } else {
      // Reset response_id when messages are fully replaced (e.g., new conversation)
      if (messagesOrUpdater.length === 0) {
        previousResponseIdRef.current = undefined;
        // The retained API content belongs to the conversation being discarded.
        apiContentByMessageIdRef.current.clear();
      }
      messagesRef.current = messagesOrUpdater;
      setMessages(messagesOrUpdater);
    }
  }, [updateMessages]);

  // Explicit "remember this" — save a specific fact to long-term memory (Brain).
  // Semantic + agent-sourced, mirroring the agent's promote_to_brain tool. No-op
  // unless memory is enabled. Best-effort, fire-and-forget.
  const archiveMemory = useCallback(
    (content: string, opts?: { sensitivity?: 'public' | 'normal' | 'sensitive' | 'secret' }) => {
      if (!enableMemoryRef.current) return;
      void archiveToMemory(
        content,
        { sensitivity: opts?.sensitivity ?? 'normal' },
        brainWorkspaceRef.current ?? 'personal',
      );
    },
    // Stable ref objects (see `useLatest`); neither invalidates this callback.
    [brainWorkspaceRef, enableMemoryRef]
  );

  return {
    messages,
    setMessages: setMessagesWithRef,
    input,
    setInput,
    isLoading,
    handleSubmit,
    stop,
    append,
    appendWithFiles,
    reload,
    retry,
    archiveMemory,
  };
}
